import React, { useEffect, useMemo, useState } from 'react';
import API from '../api';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement,
  ArcElement, Tooltip, Legend,
} from 'chart.js';
import {
  FaDollarSign, FaConciergeBell, FaReceipt,
  FaFileInvoiceDollar, FaChartBar, FaChartPie, FaUsers,
} from 'react-icons/fa';
import './Revenue.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const currency = (n = 0) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DATE_PRESETS = [
  { key: 'today',     label: 'Today' },
  { key: 'thisWeek',  label: 'This Week' },
  { key: 'lastWeek',  label: 'Last Week' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'custom',    label: 'Custom' },
];

function getPresetDates(key) {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (key === 'today') {
    return { from: iso(now), to: iso(now) };
  }
  if (key === 'thisWeek') {
    const day = now.getDay();
    const mon = new Date(now);
    mon.setDate(now.getDate() + (day === 0 ? -6 : 1 - day));
    return { from: iso(mon), to: iso(now) };
  }
  if (key === 'lastWeek') {
    const day = now.getDay();
    const thisMon = new Date(now);
    thisMon.setDate(now.getDate() + (day === 0 ? -6 : 1 - day));
    const lastMon = new Date(thisMon);
    lastMon.setDate(thisMon.getDate() - 7);
    const lastSun = new Date(thisMon);
    lastSun.setDate(thisMon.getDate() - 1);
    return { from: iso(lastMon), to: iso(lastSun) };
  }
  if (key === 'thisMonth') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: iso(first), to: iso(now) };
  }
  return null;
}

const TABS = [
  { key: 'total',    label: 'Total Overview', icon: <FaChartBar /> },
  { key: 'employee', label: 'By Employee',    icon: <FaUsers /> },
  { key: 'service',  label: 'By Service',     icon: <FaConciergeBell /> },
];

const Revenue = () => {
  const [businessId,    setBusinessId]    = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [invoices,      setInvoices]      = useState([]);
  const [employeeNames, setEmployeeNames] = useState({});
  const [serviceNames,  setServiceNames]  = useState({});
  const [activeTab,     setActiveTab]     = useState('total');
  const [shouldFetch,   setShouldFetch]   = useState(false);
  const [datePreset,    setDatePreset]    = useState('today');

  const todayISO = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(todayISO);
  const [toDate,   setToDate]   = useState(todayISO);

  /* bootstrap user + business id */
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return;
    try {
      const parsed = JSON.parse(storedUser);
      const id = parsed.role === 'admin' ? parsed.id : parsed.business_id;
      localStorage.setItem('id', id);
      setBusinessId(id);
    } catch (e) { console.error('Failed to parse user', e); }
  }, []);

  /* fetch data */
  useEffect(() => {
    const run = async () => {
      if (!businessId) return;
      setLoading(true);
      try {
        const [invRes, empRes, svcRes] = await Promise.all([
          API.get(`/auth/invoices?businessId=${businessId}`),
          API.get('/auth/employees'),
          API.get('/auth/services'),
        ]);

        const all  = invRes.data || [];
        const from = new Date(fromDate);
        const to   = new Date(toDate);
        to.setHours(23, 59, 59, 999);

        const filtered = all.filter(inv => {
          const dt = new Date(inv.createdAt);
          return dt >= from && dt <= to;
        });

        const eMap = {};
        (empRes.data || []).forEach(e => {
          if (!e) return;
          const name = e.name || e.fullName || e.displayName || e.email || 'Unknown';
          if (e._id)         eMap[e._id]         = name;
          if (e.id)          eMap[e.id]           = name;
          if (e.userId)      eMap[e.userId]       = name;
          if (e.business_id) eMap[e.business_id]  = name;
          if (e.email)       eMap[e.email]        = name;
        });

        const sMap = {};
        (svcRes.data || []).forEach(s => { sMap[s._id] = s.name; });

        setInvoices(filtered);
        setEmployeeNames(eMap);
        setServiceNames(sMap);
      } catch (e) { console.error(e); }
      finally { setLoading(false); setShouldFetch(false); }
    };
    run();
  }, [businessId, shouldFetch]);

  /* derive totals */
  const { serviceRevenue, miscRevenue, revenueByEmployee, revenueByService } = useMemo(() => {
    let serviceRev = 0, miscRev = 0;
    const byEmployee = {};
    const byService  = {};

    invoices.forEach(inv => {
      const s = inv.serviceTotal || 0;
      const m = inv.miscTotal    || 0;
      serviceRev += s;
      miscRev    += m;

      if (inv.employeeId) {
        let empKey = inv.employeeId;
        if (typeof empKey === 'object') {
          empKey = empKey._id || empKey.id || empKey.business_id || empKey.userId || empKey.email || null;
        }
        if (!empKey) empKey = inv.employee_id || inv.employee || inv.assignedTo || null;
        if (empKey) byEmployee[empKey] = (byEmployee[empKey] || 0) + s + m;
      }

      if (Array.isArray(inv.serviceIds)) {
        inv.serviceIds.forEach(id => { byService[id] = (byService[id] || 0) + s; });
      }
    });

    return { serviceRevenue: serviceRev, miscRevenue: miscRev, revenueByEmployee: byEmployee, revenueByService: byService };
  }, [invoices]);

  useEffect(() => {
    if (!revenueByEmployee || !employeeNames) return;
    const unknowns = Object.keys(revenueByEmployee).filter(k => !employeeNames[k]);
    if (unknowns.length) console.warn('Unmapped employee keys:', unknowns);
  }, [revenueByEmployee, employeeNames]);

  const totalRevenue = (serviceRevenue || 0) + (miscRevenue || 0);
  const invoiceCount = invoices.length;

  /* daily revenue chart */
  const dailyChartData = useMemo(() => {
    const map = {};
    invoices.forEach(inv => {
      const day = new Date(inv.createdAt).toISOString().slice(0, 10);
      map[day] = (map[day] || 0) + (inv.serviceTotal || 0) + (inv.miscTotal || 0);
    });
    const sorted = Object.keys(map).sort();
    return {
      labels: sorted.map(d =>
        new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      ),
      datasets: [{
        label: 'Revenue',
        data: sorted.map(d => map[d]),
        backgroundColor: '#4f46e5',
        borderColor: '#4338ca',
        borderWidth: 0,
        borderRadius: 6,
        hoverBackgroundColor: '#6366f1',
      }],
    };
  }, [invoices]);

  const doughnutData = {
    labels: ['Service Revenue', 'Misc Revenue'],
    datasets: [{
      data: [serviceRevenue, miscRevenue],
      backgroundColor: ['#4f46e5', '#10b981'],
      borderColor:     ['#4338ca', '#059669'],
      borderWidth: 2,
      hoverOffset: 6,
    }],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => ` ${currency(ctx.raw)}` } },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 12 }, color: '#8c959f' },
      },
      y: {
        grid: { color: '#f3f4f6' },
        ticks: { font: { size: 12 }, color: '#8c959f', callback: (v) => `$${v}` },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${currency(ctx.raw)}` } },
    },
  };

  /* preset handler */
  const handlePreset = (key) => {
    setDatePreset(key);
    if (key !== 'custom') {
      const dates = getPresetDates(key);
      setFromDate(dates.from);
      setToDate(dates.to);
    }
  };

  const employeeEntries = Object.entries(revenueByEmployee).sort((a, b) => b[1] - a[1]);
  const serviceEntries  = Object.entries(revenueByService).sort((a, b) => b[1] - a[1]);
  const maxEmpRev = employeeEntries[0]?.[1] || 1;
  const maxSvcRev = serviceEntries[0]?.[1]  || 1;

  return (
    <div className="rev-page">

      {/* ── Header ── */}
      <div className="rev-header">
        <h2 className="rev-title">Business Revenue</h2>
        <p className="rev-subtitle">Track and analyze revenue across dates, employees, and services</p>
      </div>

      {/* ── Controls ── */}
      <div className="rev-controls-card">
        <div className="rev-presets">
          {DATE_PRESETS.map(p => (
            <button
              key={p.key}
              className={`rev-pill ${datePreset === p.key ? 'active' : ''}`}
              onClick={() => handlePreset(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="rev-controls-row">
          <div className="rev-control-group">
            <label className="rev-label">From Date</label>
            <input
              type="date"
              className="rev-date-input"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setDatePreset('custom'); }}
            />
          </div>
          <div className="rev-control-group">
            <label className="rev-label">To Date</label>
            <input
              type="date"
              className="rev-date-input"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setDatePreset('custom'); }}
            />
          </div>
          <div className="rev-control-group rev-calc-group">
            <button className="rev-report-btn" onClick={() => setShouldFetch(true)}>
              Show Report
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="rev-loading">Loading report…</div>
      ) : (
        <>
          {/* ── Stat Cards ── */}
          <div className="rev-stats-row">
            <div className="rev-stat-card rev-stat-indigo">
              <div className="rev-stat-icon"><FaDollarSign /></div>
              <div className="rev-stat-body">
                <div className="rev-stat-label">Total Revenue</div>
                <div className="rev-stat-value">{currency(totalRevenue)}</div>
              </div>
            </div>
            <div className="rev-stat-card rev-stat-blue">
              <div className="rev-stat-icon"><FaConciergeBell /></div>
              <div className="rev-stat-body">
                <div className="rev-stat-label">Service Revenue</div>
                <div className="rev-stat-value">{currency(serviceRevenue)}</div>
              </div>
            </div>
            <div className="rev-stat-card rev-stat-green">
              <div className="rev-stat-icon"><FaReceipt /></div>
              <div className="rev-stat-body">
                <div className="rev-stat-label">Misc Revenue</div>
                <div className="rev-stat-value">{currency(miscRevenue)}</div>
              </div>
            </div>
            <div className="rev-stat-card rev-stat-amber">
              <div className="rev-stat-icon"><FaFileInvoiceDollar /></div>
              <div className="rev-stat-body">
                <div className="rev-stat-label">Invoices</div>
                <div className="rev-stat-value">{invoiceCount}</div>
              </div>
            </div>
          </div>

          {/* ── Tabs ── */}
          <div className="rev-tabs">
            {TABS.map(t => (
              <button
                key={t.key}
                className={`rev-tab ${activeTab === t.key ? 'active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>

          {/* ── Total Overview ── */}
          {activeTab === 'total' && (
            <div className="rev-charts-row">

              {/* Bar: daily revenue */}
              <div className="rev-chart-card">
                <div className="rev-card-header">
                  <FaChartBar className="rev-card-icon" />
                  <span>Daily Revenue</span>
                </div>
                {dailyChartData.labels.length > 0 ? (
                  <div className="rev-bar-wrap">
                    <Bar data={dailyChartData} options={barOptions} />
                  </div>
                ) : (
                  <div className="rev-empty-chart">No revenue data for this period</div>
                )}
              </div>

              {/* Doughnut: service vs misc */}
              <div className="rev-chart-card">
                <div className="rev-card-header">
                  <FaChartPie className="rev-card-icon" />
                  <span>Revenue Split</span>
                </div>
                <div className="rev-doughnut-outer">
                  <div className="rev-doughnut-wrap">
                    <Doughnut data={doughnutData} options={doughnutOptions} />
                    <div className="rev-doughnut-center">
                      <div className="rev-doughnut-total">{currency(totalRevenue)}</div>
                      <div className="rev-doughnut-label">total</div>
                    </div>
                  </div>
                  <div className="rev-doughnut-legend">
                    <div className="rev-legend-item">
                      <span className="rev-legend-dot" style={{ background: '#4f46e5' }} />
                      <span className="rev-legend-name">Services</span>
                      <span className="rev-legend-val">{currency(serviceRevenue)}</span>
                    </div>
                    <div className="rev-legend-item">
                      <span className="rev-legend-dot" style={{ background: '#10b981' }} />
                      <span className="rev-legend-name">Misc</span>
                      <span className="rev-legend-val">{currency(miscRevenue)}</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* ── By Employee ── */}
          {activeTab === 'employee' && (
            <div className="rev-table-card">
              <div className="rev-card-header">
                <FaUsers className="rev-card-icon" />
                <span>Revenue by Employee</span>
              </div>
              {employeeEntries.length === 0 ? (
                <div className="rev-empty">No employee revenue data for this period.</div>
              ) : (
                <div className="rev-table-scroll">
                  <table className="rev-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Contribution</th>
                        <th>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeEntries.map(([empId, amount]) => (
                        <tr key={empId}>
                          <td className="rev-name-cell">
                            {employeeNames[empId] || 'Unknown Employee'}
                          </td>
                          <td className="rev-bar-cell">
                            <div className="rev-inline-bar-track">
                              <div
                                className="rev-inline-bar-fill"
                                style={{ width: `${(amount / maxEmpRev) * 100}%` }}
                              />
                            </div>
                            <span className="rev-pct">
                              {Math.round((amount / (totalRevenue || 1)) * 100)}%
                            </span>
                          </td>
                          <td className="rev-amount-cell">{currency(amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2}><strong>Total</strong></td>
                        <td><strong>{currency(totalRevenue)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── By Service ── */}
          {activeTab === 'service' && (
            <div className="rev-table-card">
              <div className="rev-card-header">
                <FaConciergeBell className="rev-card-icon" />
                <span>Revenue by Service</span>
              </div>
              {serviceEntries.length === 0 ? (
                <div className="rev-empty">No service revenue data for this period.</div>
              ) : (
                <div className="rev-table-scroll">
                  <table className="rev-table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Contribution</th>
                        <th>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serviceEntries.map(([svcId, amount]) => (
                        <tr key={svcId}>
                          <td className="rev-name-cell">
                            {serviceNames[svcId] || `Service ${svcId}`}
                          </td>
                          <td className="rev-bar-cell">
                            <div className="rev-inline-bar-track">
                              <div
                                className="rev-inline-bar-fill rev-fill-green"
                                style={{ width: `${(amount / maxSvcRev) * 100}%` }}
                              />
                            </div>
                            <span className="rev-pct">
                              {Math.round((amount / (totalRevenue || 1)) * 100)}%
                            </span>
                          </td>
                          <td className="rev-amount-cell">{currency(amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2}><strong>Total</strong></td>
                        <td><strong>{currency(totalRevenue)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

        </>
      )}
    </div>
  );
};

export default Revenue;
